import {match} from 'ts-pattern';

import {MutableRefObject, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNotNil} from 'ramda';

import {useDebouncedCallback, useDidUpdate} from 'shared/web';

import {
  onlineOfferApi,
  useUpdateFinancialProductPreferenceMutation,
} from '../../../api/onlineOfferApi';
import {
  FinancialProductPreferenceCoordinates,
  FinancialProductPreferenceRefusalSchema,
  FinancialProductType,
  GetOnlineOfferFinancialProductsApiArg,
  PublicFinancialProductsResponseBody,
} from '../../../api/types/onlineOfferTypes';
import {AppDispatch} from '../../../store';
import {
  FinancialProductPreferenceOutbox,
  FinancialProductPreferenceOutboxEntry,
} from '../FinancialProductPreferenceOutbox';
import {pickAuthoritativeFinancialProducts} from '../pickAuthoritativeFinancialProducts';
import {acknowledgesOutboxEntry} from './acknowledgesOutboxEntry';
import {isRetryableWriteError} from './isRetryableWriteError';
import {useConflictNotice} from './useConflictNotice';

// Coalesces a released slider with a select change made right after it into one write.
const SAVE_DEBOUNCE_MS = 300;
const BACKGROUND_RETRY_MS = 5_000;

export type QuoteLifecycle = 'ready' | 'pending' | 'failed';

type PreferenceWriteAttempt = {
  id: number;
  closedBoundaryAtStart: number;
  entry: FinancialProductPreferenceOutboxEntry;
};

type PreferenceWriteRuntime = {
  currentWriteAttempt: PreferenceWriteAttempt | null;
  pendingEntry: FinancialProductPreferenceOutboxEntry | null;
  currentDraft: FinancialProductPreferenceCoordinates | null;
  // Kept in the runtime ref because follow-up writes read it synchronously, so each
  // one carries the revision produced by its predecessor.
  highestKnownRevision: number;
  // A conflict caused by writing below an already-known revision is this surface's own;
  // nobody else changed the offer, so no "seller changed" notice should appear.
  selfInflictedConflictRevision: number | null;
  nextWriteAttemptId: number;
  backgroundRetryTimer: ReturnType<typeof setTimeout> | null;
  currentAddress: GetOnlineOfferFinancialProductsApiArg | null;
  lastClosedSnapshotReceivedAt: number;
  currentHasAuthoritativeClosedRead: boolean;
  // A restored intent waits for the first read, which decides whether the offer is closed.
  isRestoredEntryAwaitingRead: boolean;
};

type UsePreferenceWriteOptions = {
  address: GetOnlineOfferFinancialProductsApiArg | null;
  financialProducts: PublicFinancialProductsResponseBody | undefined;
  financialProductsFulfilledAt: number | undefined;
  hasAuthoritativeClosedRead: boolean;
  isActiveOwner: MutableRefObject<boolean>;
  restoredEntry: FinancialProductPreferenceOutboxEntry | null;
  refresh: () => void;
  setOpenedProduct: (productType: FinancialProductType | null) => void;
};

const toFinancialProductPreferenceRefusal = (error: unknown) =>
  match(FinancialProductPreferenceRefusalSchema.safeParse(error))
    .with({success: true}, (parsed) => parsed.data)
    .otherwise(() => null);

const isSameIntent = (
  first: FinancialProductPreferenceOutboxEntry | null,
  second: FinancialProductPreferenceOutboxEntry
) => isNotNil(first) && FinancialProductPreferenceOutbox.isSameIntent(first, second);

const isSameAttempt = (
  first: FinancialProductPreferenceOutboxEntry | null,
  second: FinancialProductPreferenceOutboxEntry
) => isNotNil(first) && FinancialProductPreferenceOutbox.isSameAttempt(first, second);

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export const usePreferenceWrite = (options: UsePreferenceWriteOptions) => {
  const dispatch = useDispatch<AppDispatch>();
  const [updatePreference, updateState] = useUpdateFinancialProductPreferenceMutation();
  const [draft, setDraft] = useState<FinancialProductPreferenceCoordinates | null>(
    options.restoredEntry?.coordinates ?? null
  );
  const runtime = useRef<PreferenceWriteRuntime>({
    currentWriteAttempt: null,
    pendingEntry: options.restoredEntry,
    currentDraft: options.restoredEntry?.coordinates ?? null,
    highestKnownRevision: 0,
    selfInflictedConflictRevision: null,
    nextWriteAttemptId: 0,
    backgroundRetryTimer: null,
    currentAddress: options.address,
    lastClosedSnapshotReceivedAt: 0,
    currentHasAuthoritativeClosedRead: options.hasAuthoritativeClosedRead,
    isRestoredEntryAwaitingRead: isNotNil(options.restoredEntry),
  });
  const {
    heldConflictNotice,
    clearHeldConflictNotice,
    releaseHeldConflictNoticeWhenDue,
    holdConflictNotice,
  } = useConflictNotice(() => isNotNil(runtime.current.currentWriteAttempt));
  runtime.current.currentAddress = options.address;
  runtime.current.currentHasAuthoritativeClosedRead = options.hasAuthoritativeClosedRead;

  if (options.hasAuthoritativeClosedRead) {
    runtime.current.lastClosedSnapshotReceivedAt = Math.max(
      runtime.current.lastClosedSnapshotReceivedAt,
      options.financialProductsFulfilledAt ?? 0
    );
  }

  const updateBelongsToCurrentLifecycle =
    (updateState.startedTimeStamp ?? 0) > runtime.current.lastClosedSnapshotReceivedAt;
  const refusalForState = updateBelongsToCurrentLifecycle
    ? toFinancialProductPreferenceRefusal(updateState.error)
    : null;
  // A mutation answer yields to the first poll completed after it — even at the same revision, because the seller may have repriced.
  const isAcknowledgementCurrent =
    updateBelongsToCurrentLifecycle &&
    (options.financialProductsFulfilledAt ?? 0) <= (updateState.fulfilledTimeStamp ?? 0);
  // A seller restoration does not bump the revision; comparing the poll prevents the
  // refused state from outranking a same-revision poll forever.
  // Rejections have no fulfilled timestamp, so their start time is the comparison boundary.
  const isRefusalCurrent =
    updateBelongsToCurrentLifecycle &&
    (options.financialProductsFulfilledAt ?? 0) <= (updateState.startedTimeStamp ?? 0);

  // Revision, not arrival order, decides which answer describes the stored offer.
  const products = options.hasAuthoritativeClosedRead
    ? options.financialProducts
    : pickAuthoritativeFinancialProducts([
        {body: options.financialProducts, authority: 'POLLED'},
        {
          body: isAcknowledgementCurrent ? updateState.data : undefined,
          authority: 'ACKNOWLEDGED',
        },
        {
          body: match(isRefusalCurrent ? refusalForState : null)
            .with({kind: 'REFUSED'}, (refused) => refused.current)
            .otherwise(() => undefined),
          authority: 'REFUSED',
        },
      ]);

  const expectedPreferenceRevision = products?.preferenceRevision ?? 0;
  runtime.current.highestKnownRevision = Math.max(
    runtime.current.highestKnownRevision,
    expectedPreferenceRevision
  );

  // The refused state still describes the offer through refused.current; only the
  // notice is withheld when this surface caused the conflict itself.
  const isSelfInflictedConflict = match(refusalForState)
    .with(
      {kind: 'REFUSED', reason: 'PREFERENCE_REVISION_CONFLICT'},
      (conflict) =>
        conflict.current.preferenceRevision === runtime.current.selfInflictedConflictRevision
    )
    .otherwise(() => false);
  const refusal = isSelfInflictedConflict ? null : refusalForState;

  const isLatestWriteForDraft =
    isNotNil(draft) &&
    updateBelongsToCurrentLifecycle &&
    isNotNil(updateState.originalArgs) &&
    FinancialProductPreferenceOutbox.matchesCoordinates(
      updateState.originalArgs.coordinates,
      draft
    );
  const isDraftAwaitingRetry =
    isLatestWriteForDraft &&
    match(updateState)
      .with({isError: true}, (failed) => isRetryableWriteError(failed.error))
      // A 200 that does not echo the sent choice is retried like a failure.
      .with({isSuccess: true}, (answered) => {
        const pendingEntry = runtime.current.pendingEntry;

        return (
          isNotNil(answered.data) &&
          isNotNil(pendingEntry) &&
          !acknowledgesOutboxEntry(answered.data, pendingEntry)
        );
      })
      .otherwise(() => false);
  // A terminal failure drops the draft and the confirmed quote is shown again, hence 'ready';
  // a retryable one keeps the draft for the retry, and no quote exists for it yet, hence 'failed'.
  const quoteLifecycle: QuoteLifecycle = match({draft, isDraftAwaitingRetry})
    .with({draft: null}, () => 'ready' as const)
    .with({isDraftAwaitingRetry: true}, () => 'failed' as const)
    .otherwise(() => 'pending' as const);

  // The retry sends the newest pending intent, never the failed attempt it replaced.
  function retryEntry() {
    if (isNotNil(runtime.current.pendingEntry)) {
      sendEntry(runtime.current.pendingEntry);
    }
  }

  const scheduleBackgroundRetry = () => {
    if (!options.isActiveOwner.current) {
      return;
    }

    if (isNotNil(runtime.current.backgroundRetryTimer)) {
      clearTimeout(runtime.current.backgroundRetryTimer);
    }

    runtime.current.backgroundRetryTimer = setTimeout(retryEntry, BACKGROUND_RETRY_MS);
  };

  const isCurrentAttempt = (attempt: PreferenceWriteAttempt) =>
    options.isActiveOwner.current && runtime.current.currentWriteAttempt?.id === attempt.id;

  const clearEntry = (entry: FinancialProductPreferenceOutboxEntry) => {
    const scope = runtime.current.currentAddress;
    const isCurrentEntry = isSameAttempt(runtime.current.pendingEntry, entry);

    if (isNotNil(scope)) {
      FinancialProductPreferenceOutbox.clear(scope, entry);
    }

    if (!isCurrentEntry) {
      return false;
    }

    runtime.current.pendingEntry = null;

    if (isNotNil(runtime.current.backgroundRetryTimer)) {
      clearTimeout(runtime.current.backgroundRetryTimer);
      runtime.current.backgroundRetryTimer = null;
    }

    return true;
  };

  // One write owns the offer at a time; an overtaking intent waits for its revision.
  const settleCurrentAttempt = (attempt: PreferenceWriteAttempt) => {
    if (!isCurrentAttempt(attempt)) {
      return;
    }

    runtime.current.currentWriteAttempt = null;

    const pending = runtime.current.pendingEntry;

    if (isNotNil(pending) && !isSameIntent(pending, attempt.entry)) {
      sendEntry(pending);
      return;
    }

    releaseHeldConflictNoticeWhenDue();

    if (!clearEntry(pending ?? attempt.entry)) {
      return;
    }

    runtime.current.currentDraft = null;
    setDraft(null);
  };

  const retryCurrentAttemptLater = (attempt: PreferenceWriteAttempt) => {
    if (!isCurrentAttempt(attempt)) {
      return;
    }

    runtime.current.currentWriteAttempt = null;
    releaseHeldConflictNoticeWhenDue();
    scheduleBackgroundRetry();
  };

  const retireDraftForClosedSnapshot = () => {
    const entry = runtime.current.pendingEntry;
    const hasDraftLifecycle =
      isNotNil(entry) ||
      isNotNil(runtime.current.currentDraft) ||
      isNotNil(runtime.current.currentWriteAttempt);

    runtime.current.isRestoredEntryAwaitingRead = false;

    if (!hasDraftLifecycle) {
      return;
    }

    runtime.current.currentWriteAttempt = null;
    runtime.current.currentDraft = null;
    clearHeldConflictNotice();
    updateState.reset();

    if (isNotNil(entry)) {
      clearEntry(entry);
    } else if (isNotNil(runtime.current.backgroundRetryTimer)) {
      clearTimeout(runtime.current.backgroundRetryTimer);
      runtime.current.backgroundRetryTimer = null;
    }

    options.setOpenedProduct(options.financialProducts?.selectedProduct ?? null);
    setDraft(null);
  };

  function sendEntry(entry: FinancialProductPreferenceOutboxEntry) {
    const scope = runtime.current.currentAddress;

    if (
      !options.isActiveOwner.current ||
      !isNotNil(scope) ||
      !isSameAttempt(runtime.current.pendingEntry, entry) ||
      isNotNil(runtime.current.currentWriteAttempt)
    ) {
      return;
    }

    // Rebase a queued entry onto the latest witnessed revision to avoid colliding with itself.
    const sentEntry =
      entry.expectedPreferenceRevision < runtime.current.highestKnownRevision
        ? {...entry, expectedPreferenceRevision: runtime.current.highestKnownRevision}
        : entry;

    if (sentEntry !== entry) {
      FinancialProductPreferenceOutbox.updateRevision(
        scope,
        entry,
        runtime.current.highestKnownRevision
      );
      runtime.current.pendingEntry = sentEntry;
    }

    const attempt = {
      id: runtime.current.nextWriteAttemptId + 1,
      closedBoundaryAtStart: runtime.current.lastClosedSnapshotReceivedAt,
      entry: sentEntry,
    };
    runtime.current.nextWriteAttemptId = attempt.id;
    runtime.current.currentWriteAttempt = attempt;

    updatePreference({
      ...scope,
      coordinates: sentEntry.coordinates,
      expectedPreferenceRevision: sentEntry.expectedPreferenceRevision,
    })
      .unwrap()
      .then((acknowledgement) => {
        // Record this before the current-attempt guard: even an overtaken acknowledgement
        // tells this surface which revision the offer is on.
        runtime.current.highestKnownRevision = Math.max(
          runtime.current.highestKnownRevision,
          acknowledgement.preferenceRevision
        );

        if (!isCurrentAttempt(attempt)) {
          return;
        }

        const isOvertaken =
          isNotNil(runtime.current.pendingEntry) &&
          !isSameIntent(runtime.current.pendingEntry, sentEntry);

        if (!acknowledgesOutboxEntry(acknowledgement, sentEntry) && !isOvertaken) {
          retryCurrentAttemptLater(attempt);
          return;
        }

        const ownsCurrentLifecycle =
          !runtime.current.currentHasAuthoritativeClosedRead &&
          attempt.closedBoundaryAtStart === runtime.current.lastClosedSnapshotReceivedAt;

        if (ownsCurrentLifecycle) {
          dispatch(
            onlineOfferApi.util.updateQueryData(
              'getOnlineOfferFinancialProducts',
              scope,
              () => acknowledgement
            )
          );
        }

        settleCurrentAttempt(attempt);
      })
      .catch((error: unknown) => {
        if (!isCurrentAttempt(attempt)) {
          return;
        }

        match(toFinancialProductPreferenceRefusal(error))
          .with({kind: 'REFUSED', reason: 'PREFERENCE_REVISION_CONFLICT'}, (conflict) => {
            const currentEntry = runtime.current.pendingEntry;

            runtime.current.currentWriteAttempt = null;

            if (!isNotNil(currentEntry)) {
              return;
            }

            // A conflict at an already-witnessed revision refusing a write aimed below it is
            // our own; at an unseen revision, or refusing the sent revision, it is somebody else's.
            const isOwnConflict =
              sentEntry.expectedPreferenceRevision < conflict.current.preferenceRevision &&
              conflict.current.preferenceRevision === runtime.current.highestKnownRevision;
            runtime.current.selfInflictedConflictRevision = isOwnConflict
              ? conflict.current.preferenceRevision
              : null;

            if (!isOwnConflict) {
              holdConflictNotice(conflict);
            }
            runtime.current.highestKnownRevision = Math.max(
              runtime.current.highestKnownRevision,
              conflict.current.preferenceRevision
            );

            const expectedRevision = Math.max(
              currentEntry.expectedPreferenceRevision,
              conflict.current.preferenceRevision
            );
            const revisedEntry = {
              ...currentEntry,
              expectedPreferenceRevision: expectedRevision,
            };
            const storedEntry = FinancialProductPreferenceOutbox.updateRevision(
              scope,
              currentEntry,
              expectedRevision
            );

            runtime.current.pendingEntry =
              isNotNil(storedEntry) && isSameIntent(storedEntry, revisedEntry)
                ? {
                    ...revisedEntry,
                    expectedPreferenceRevision: Math.max(
                      revisedEntry.expectedPreferenceRevision,
                      storedEntry.expectedPreferenceRevision
                    ),
                  }
                : revisedEntry;
            options.refresh();

            // A newly named revision is retried immediately; otherwise only the retry tick repeats it.
            const hasNewRevisionToAimAt =
              runtime.current.pendingEntry.expectedPreferenceRevision >
                sentEntry.expectedPreferenceRevision ||
              !isSameIntent(runtime.current.pendingEntry, sentEntry);

            if (hasNewRevisionToAimAt) {
              sendEntry(runtime.current.pendingEntry);
            } else {
              releaseHeldConflictNoticeWhenDue();
              scheduleBackgroundRetry();
            }
          })
          .with({kind: 'REFUSED'}, () => settleCurrentAttempt(attempt))
          .with({kind: 'UNREACHABLE'}, () => retryCurrentAttemptLater(attempt))
          .otherwise(() => {
            if (isRetryableWriteError(error)) {
              retryCurrentAttemptLater(attempt);
            } else {
              settleCurrentAttempt(attempt);
            }
          });
      });
  }

  const resendPendingEntry = () => {
    if (isNotNil(runtime.current.pendingEntry)) {
      sendEntry(runtime.current.pendingEntry);
    }
  };

  // A restored intent is sent as soon as a live read shows the offer still takes writes;
  // callers retire it instead when that read is a closed snapshot.
  const sendRestoredEntryOnceRead = () => {
    if (!runtime.current.isRestoredEntryAwaitingRead || !isNotNil(options.financialProducts)) {
      return;
    }

    runtime.current.isRestoredEntryAwaitingRead = false;
    resendPendingEntry();
  };

  useDidUpdate(() => {
    if (options.hasAuthoritativeClosedRead) {
      retireDraftForClosedSnapshot();
      return;
    }

    sendRestoredEntryOnceRead();
  }, [options.financialProducts, options.financialProductsFulfilledAt]);

  // Read the entry at fire time so the newest queued intent is the one sent.
  const scheduleSave = useDebouncedCallback(resendPendingEntry, SAVE_DEBOUNCE_MS);

  return {
    products,
    refusalForState,
    refusal,
    isRefusalCurrent,
    heldConflictNotice,
    draft,
    quoteLifecycle,
    updateDraft(coordinates: FinancialProductPreferenceCoordinates) {
      runtime.current.currentDraft = coordinates;
      setDraft(coordinates);
    },
    queuePreferenceWrite(coordinates: FinancialProductPreferenceCoordinates) {
      if (isNotNil(options.address)) {
        runtime.current.pendingEntry = FinancialProductPreferenceOutbox.replace(
          options.address,
          coordinates,
          runtime.current.highestKnownRevision
        );
        scheduleSave();
      }
    },
    startWriteOwnership() {
      window.addEventListener('online', resendPendingEntry);
    },
    clearCurrentWriteAttempt() {
      runtime.current.currentWriteAttempt = null;
    },
    finishWriteOwnership() {
      clearHeldConflictNotice();
      window.removeEventListener('online', resendPendingEntry);

      if (isNotNil(runtime.current.backgroundRetryTimer)) {
        clearTimeout(runtime.current.backgroundRetryTimer);
        runtime.current.backgroundRetryTimer = null;
      }
    },
    retireDraftForClosedSnapshot,
    sendRestoredEntryOnceRead,
  };
};
